// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import FillCircle from "@insite/mobius/Icons/FillCircle";
import { resolveColor } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useMergeProps from "@toolkit/Framework/Common/Hooks/useMergeProps";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import cssProp from "@toolkit/Framework/Common/Utilities/cssProp";
import { DataOption, DataOptions } from "@toolkit/Framework/Common/Utilities/dataOptions";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import React, { FC, PropsWithChildren } from "react";
import { css } from "styled-components";

export type StatusTypeOption = DataOption<never>;
export interface StatusTypeOptionMapper {
    green: StatusTypeOption;
    yellow: StatusTypeOption;
    grey: StatusTypeOption;
    red: StatusTypeOption;
}
export type StatusTypeOptions = DataOptions<StatusTypeOption> & StatusTypeOptionMapper;
export type StatusType = keyof StatusTypeOptionMapper;

export const statusTypeOptions: StatusTypeOptions = {
    green: { id: "green", displayName: "Green" },
    yellow: { id: "yellow", displayName: "Yellow" },
    grey: { id: "grey", displayName: "Grey" },
    red: { id: "red", displayName: "Red" },
};

export type StatusAlignOption = DataOption<never>;
export interface StatusAlignOptionMapper {
    left: StatusAlignOption;
    center: StatusAlignOption;
    right: StatusAlignOption;
}
export type StatusAlignOptions = DataOptions<StatusAlignOption> & StatusAlignOptionMapper;
export type StatusAlign = keyof StatusAlignOptionMapper;

export const statusAlignOptions: StatusAlignOptions = {
    left: { id: "left", displayName: "Left" },
    center: { id: "center", displayName: "Center" },
    right: { id: "right", displayName: "Right" },
};

export interface StatusProps {
    type?: StatusType;
    align?: StatusAlign;
    showIcon?: boolean;
    extendedStyles?: StatusStyles;
}

export interface StatusStyles {
    spacing?: string;
    color?: string;
    backgroundColor?: string;
    wrapper?: InjectableCss;
    icon?: IconPresentationProps;
    content?: InjectableCss;
}

export const statusStyles: StatusStyles = {
    color: "text.light",
    backgroundColor: "comment.accent",
    spacing: "0.5em",
    wrapper: {
        css: css`
            ${spacingStyles()}
            width: 100%;
            display: flex;
            align-items: flex-start;

            ${({ theme }) => `
                ${cssProp("font-size", theme.design.typography.size.smallest)}
                ${cssProp("line-height", theme.design.typography.lineHeight.semi)}
            `}

            ul, ol {
                ${spacingStyles("0.5em", "0.5em")}
            }

            li {
                ${spacingStyles(null, "0.25em")}
            }

            ${breakpoint("sm")} {
                ${x => cssProp("font-size", x.theme.design.typography.size.small)}
            }
        `,
    },
    content: {},
    icon: {
        src: FillCircle,
        color: "currentColor",
        size: 8,
        css: css`
            margin-top: ${pxToRem(6)};
        `,
    },
};

export const statusTypes: Partial<Record<StatusType, StatusStyles>> = {
    green: {
        color: "success",
    },
    yellow: {
        color: "warning",
    },
    grey: {
        color: "neutral",
    },
    red: {
        color: "danger",
    },
};

export const statusAlign: Partial<Record<StatusAlign, StatusStyles>> = {
    left: {
        wrapper: {
            css: css`
                justify-content: flex-start;
            `,
        },
    },
    center: {
        wrapper: {
            css: css`
                justify-content: center;
            `,
        },
    },
    right: {
        wrapper: {
            css: css`
                justify-content: flex-end;
            `,
        },
    },
};

const Status: FC<PropsWithChildren<StatusProps>> = ({
    type,
    align = "left",
    showIcon = false,
    children,
    extendedStyles,
    ...htmlAttributes
}) => {
    const textAlignDefinition = align ? statusAlign[align] : {};
    const typeDefinition = type ? statusTypes[type] : {};
    const definitions = useMergeProps(textAlignDefinition, typeDefinition);
    const stylesAndDefinitions = useMergeProps(statusStyles, definitions);
    const styles = useMergeProps(stylesAndDefinitions, extendedStyles);

    if (!children) {
        return null;
    }

    const wrapperStyles = css`
        ${cssProp("padding", styles.spacing)}
        ${({ theme }) => `
            ${cssProp("color", styles.color, x => resolveColor(x, theme))}
            ${cssProp("background-color", `color-mix(in srgb, ${resolveColor(styles.color, theme)} 10%, white)`)}
            `}
        ${styles.wrapper?.css}
    `;

    const iconStyles = css`
        ${cssProp("margin-right", styles.spacing)}
        ${styles.icon?.css}
    `;

    return (
        <StyledWrapper {...styles.wrapper} css={wrapperStyles} {...htmlAttributes}>
            {showIcon && <Icon {...styles.icon} css={iconStyles} />}
            <StyledWrapper {...styles.content}>{children}</StyledWrapper>
        </StyledWrapper>
    );
};

export default Status;
